import React, {useEffect, useState} from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import {ArrowDropDown} from '@material-ui/icons';
import {FormattedMessage, useIntl} from 'react-intl';
import {useHistory} from 'react-router';
import {Pagination} from '@material-ui/lab';
import {useSelector} from 'react-redux';
import CopyToClipboard from 'react-copy-to-clipboard';
import {RootState, useAppDispatch} from '../../../store';
import {
  deleteInferenceApp,
  fetchInferenceApps,
} from '../../../store/prodDeployments/dashboard';
import {
  InferenceApplicationsRequest,
  InferenceAppSortableColumn,
} from '../../../types/deployment/InferenceApplicationsRequest';
import {StudioKebabMenu} from '../../../base-components/StudioKebabMenu';
import {toast} from '../../../base-components/StudioToast';
import {InferenceApplicationInfo} from '../../../types/deployment/InferenceApplicationsResponse';
import {ConfirmDialog} from '../../../base-components/StudioConfirmDialog';
import {ContentCopyIcon} from '../../ContentCopyIcon/ContentCopyIcon';
import {RuntimeType} from '../../../types/model/framework/RuntimeType';
import {
  getInferenceApplications,
  getInferenceApplicationsCount,
} from './../../../store/prodDeployments/selectors';
import {isNumber} from 'lodash';
import {StudioTooltip} from '../../../base-components/StudioTooltip';
import {REQUEST_POLLING_INTERVAL} from '../../../config/constants';

import './InferenceAppsTable.scss';

const PAGE_SIZE = 7;

export const InferenceAppsTable = ({search}: InferenceAppsTableProps) => {
  const history = useHistory();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [sort, setSort] = useState<InferenceAppSortableColumn>('name');
  const [sortDir, setSortDir] = useState<-1 | 1>(1);
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    item: InferenceApplicationRow | null;
    disableSubmitButton?: boolean;
  }>({isOpen: false, item: null, disableSubmitButton: false});
  const [page, setPage] = useState(0);
  const userId = useSelector((state: RootState) => state.user.userId);
  const groupId = useSelector((state: RootState) => state.user.groupId);

  useEffect(() => {
    if (groupId) {
      const requestBody: InferenceApplicationsRequest = {
        firstPage: page,
        pageSize: PAGE_SIZE,
        paginated: true,
        orderDefinitions: [{field: sort, direction: sortDir === -1 ? 'DESC' : 'ASC'}],
        params: {
          name: search,
        },
        groupId,
      };

      let promise = dispatch(fetchInferenceApps(requestBody));
      const interval = setInterval(() => {
        promise = dispatch(fetchInferenceApps(requestBody));
      }, REQUEST_POLLING_INTERVAL);

      return () => {
        promise.abort();
        clearInterval(interval);
      };
    }
  }, [search, sort, sortDir, page, groupId, dispatch]);

  const inferenceApps = useSelector(getInferenceApplications);
  const inferenceAppsCount = useSelector(getInferenceApplicationsCount);

  const paginationCount =
    Math.ceil(inferenceAppsCount / PAGE_SIZE) === 0
      ? 1
      : Math.ceil(inferenceAppsCount / PAGE_SIZE);
  const paginationPage = page + 1;

  type InferenceApplicationRow = {
    num: string;
    id: string;
    ownerId: string;
    applicationId: string;
    deploymentId: string | undefined;
    projectId: string;
    applicationName: string;
    deploymentName: string | undefined;
    deviceName: string | undefined;
    projectName: string;
    type: string;
    datasetName: string;
    modelName: string;
    batchInferenceUrl: string;
    inferencePageUrl: string;
    runtimeType: RuntimeType;
    inputType: string;
  };

  const expandedApps: Array<InferenceApplicationRow> = new Array<
    InferenceApplicationRow
  >();
  let totalResults = 0;

  inferenceApps.forEach((app: InferenceApplicationInfo) => {
    let appRow: InferenceApplicationRow = {
      num: totalResults.toString(),
      id: app.id,
      ownerId: app?.ownerId,
      applicationId: app.applicationId,
      deploymentId: app.deploymentId,
      projectId: app.projectId,
      applicationName: app.name,
      deviceName: app.targetDeviceName,
      deploymentName: app.deploymentName,
      projectName: app.projectName,
      type: app.targetType
        ? app.targetType.charAt(0) + app.targetType.slice(1).toLowerCase()
        : '',
      datasetName: app.datasetName ?? '',
      modelName: app.modelName,
      batchInferenceUrl: app.batchInferenceUrl,
      inferencePageUrl: app.inferencePageUrl,
      runtimeType: app.runtimeType,
      inputType: app.inputType
        ? app.inputType.charAt(0) + app.inputType.slice(1).toLowerCase()
        : '',
    };

    expandedApps.push(appRow);

    totalResults += 1;
  });

  const handleSort = (column: InferenceAppSortableColumn) => {
    if (column === sort) {
      setSortDir(sortDir === 1 ? -1 : 1);
    } else {
      setSort(column);
      setSortDir(1);
    }
  };

  const getSortDirection = (): 'asc' | 'desc' => {
    return sortDir === 1 ? 'asc' : 'desc';
  };

  const buildURL = (path: string) => {
    const {hostname, protocol, port} = window.location;
    if (port) {
      return `${protocol}//${hostname}:${port}${path}`;
    }
    return `${protocol}//${hostname}${path}`;
  };

  return (
    <>
      <TableContainer
        component={Paper}
        className="prod-deploy-dashboard__deployments"
        elevation={0}
      >
        <Table stickyHeader className="prod-deploy-dashboard__table">
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sort === 'name'}
                  direction={getSortDirection()}
                  onClick={() => handleSort('name')}
                  IconComponent={ArrowDropDown}
                >
                  <FormattedMessage id="prodDeployment.applicationName" />
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.deploymentName" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.device" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.project" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.type" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.inputType" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.dataset" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.model" />
              </TableCell>
              <TableCell>
                <FormattedMessage id="prodDeployment.api" />
              </TableCell>
              <TableCell />
              <TableCell>
                <FormattedMessage id="prodDeployment.singlePage" />
              </TableCell>
              <TableCell />
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {expandedApps.length ? (
              expandedApps.map(row => {
                return (
                  <TableRow key={row.num} className="prod-deploy-dashboard__row">
                    <TableCell>{row.applicationName}</TableCell>
                    <TableCell>{row.deploymentName || row.applicationName}</TableCell>
                    <TableCell>{row.deviceName}</TableCell>
                    <TableCell>{row.projectName}</TableCell>
                    <TableCell>{row.type}</TableCell>
                    <TableCell>{row.inputType}</TableCell>
                    <TableCell>{row.datasetName}</TableCell>
                    <TableCell>{row.modelName}</TableCell>
                    <TableCell className="url_cell">
                      {row.batchInferenceUrl && (
                        <a href={row.batchInferenceUrl}>
                          {buildURL(row.batchInferenceUrl)}
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="copy_icon">
                      {row.batchInferenceUrl && (
                        <CopyToClipboard
                          text={buildURL(row.batchInferenceUrl)}
                          onCopy={() => {
                            toast.success('Copied to clipboard!');
                          }}
                        >
                          <span>
                            <ContentCopyIcon />
                          </span>
                        </CopyToClipboard>
                      )}
                    </TableCell>
                    <TableCell className="url_open_cell">
                      {row.inferencePageUrl && (
                        <a target="_blank" href={row.inferencePageUrl} rel="noreferrer">
                          open
                        </a>
                      )}
                    </TableCell>
                    <TableCell className="copy_icon">
                      {row.inferencePageUrl && (
                        <StudioTooltip
                          title={intl.formatMessage({
                            id: 'prodDeployment.copySPAApplicationUrl',
                          })}
                          placement="top"
                        >
                          <CopyToClipboard
                            text={buildURL(row.inferencePageUrl)}
                            onCopy={() => {
                              toast.success('Copied to clipboard!');
                            }}
                          >
                            <span>
                              <ContentCopyIcon />
                            </span>
                          </CopyToClipboard>
                        </StudioTooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      <StudioKebabMenu
                        items={[
                          {
                            title: intl.formatMessage({
                              id: 'prodDeployment.configureSPA',
                            }),
                            onClick: () => {
                              if (row.type === 'Production') {
                                history.push(
                                  `/project/${row.projectId}/inference/${row.applicationId}/deployment/${row.deploymentId}/configure?spaId=${row.id}`
                                );
                              } else if (row.type === 'Test') {
                                history.push(
                                  `/project/${row.projectId}/inference/${row.applicationId}/create?spaId=${row.id}`
                                );
                              }
                            },
                            disabled: row?.ownerId !== userId,
                          },
                          {
                            title: intl.formatMessage({
                              id: 'prodDeployment.viewDetails',
                            }),
                            onClick: () => {
                              history.push(
                                `/spa/project/${row.projectId}/application/${row.applicationId}/inference/${row.id}/details`
                              );
                            },
                          },
                          {
                            title: intl.formatMessage({
                              id: 'form.delete',
                            }),
                            onClick: async () => {
                              setDeleteConfirmation({isOpen: true, item: row});
                            },
                            disabled: row?.ownerId !== userId,
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                );
              })
            ) : (
              <TableRow>
                <TableCell colSpan={100} className="prod-deploy-dashboard__no-results">
                  <FormattedMessage id="dashboard.noResults" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {isNumber(inferenceAppsCount) && (
        <Pagination
          className="prod-deploy-dashboard__pagination"
          count={paginationCount}
          page={paginationPage}
          shape="rounded"
          onChange={(_, page) => setPage(page - 1)}
          data-testid="prod-deploy-dashboard-pagination"
          id="prod-deploy-dashboard-pagination"
        />
      )}

      <ConfirmDialog
        type="confirm"
        title={intl.formatMessage({id: 'prodDeployment.deleteAppConfirmTitle'})}
        message={intl.formatMessage({id: 'prodDeployment.deleteAppConfirmMessage'})}
        submitLabel={intl.formatMessage({id: 'prodDeployment.deleteConfirmSubmitLabel'})}
        open={deleteConfirmation.isOpen}
        onClose={() => setDeleteConfirmation({isOpen: false, item: null})}
        disableSubmit={deleteConfirmation.disableSubmitButton}
        onOk={async () => {
          if (deleteConfirmation.item) {
            setDeleteConfirmation({...deleteConfirmation, disableSubmitButton: true});
            await dispatch(
              deleteInferenceApp({
                applicationId: deleteConfirmation.item.applicationId,
                spaId: deleteConfirmation.item.id,
              })
            );
            toast.success(`Successfully deleted application`);
          }

          setDeleteConfirmation({isOpen: false, item: null});
        }}
      />
    </>
  );
};

type InferenceAppsTableProps = {
  search: string;
};
